import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Box,
  Grid,
  Tooltip,
  CircularProgress,
  makeStyles,
  Link,
  Tabs,
  Tab
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Email as EmailTemplateIcon
} from "@material-ui/icons";
import useSMTPConfig from "../hooks/useSMTPConfig";
import EmailTemplateManager from "./EmailTemplateManager";
import { i18n } from "../../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.1)'
  },
  table: {
    minWidth: 650,
    backgroundColor: theme.palette.background.paper
  },
  statusChip: {
    marginRight: theme.spacing(1)
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
    backgroundColor: theme.palette.background.paper
  },
  formField: {
    marginBottom: theme.spacing(2)
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end"
  },
  infoBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.mode === 'dark' 
      ? 'rgba(33, 150, 243, 0.1)' 
      : 'rgba(33, 150, 243, 0.05)',
    borderRadius: 8,
    border: `1px solid ${theme.mode === 'dark' ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.2)'}`
  },
  errorModal: {
    "& .MuiDialog-paper": {
      backgroundColor: theme.palette.background.paper
    }
  },
  errorContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.mode === 'dark' 
      ? 'rgba(211, 47, 47, 0.1)' 
      : 'rgba(211, 47, 47, 0.05)',
    borderRadius: 8,
    border: `1px solid ${theme.mode === 'dark' ? 'rgba(211, 47, 47, 0.3)' : 'rgba(211, 47, 47, 0.2)'}`,
    marginTop: theme.spacing(2)
  },
  tabsContainer: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  tabPanel: {
    paddingTop: theme.spacing(2)
  }
}));

const SMTPConfigManager = () => {
  const classes = useStyles();
  const {
    loading,
    configs,
    selectedConfig,
    testing,
    createConfig,
    updateConfig,
    deleteConfig,
    testConnection,
    setSelectedConfig
  } = useSMTPConfig();

  const [internalTab, setInternalTab] = useState(0); // 0: Configuraciones SMTP, 1: Plantillas Email
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    provider: "custom",
    host: "",
    port: 587,
    secure: false,
    requireTLS: true,
    user: "",
    password: "",
    from: "",
    isActive: true,
    isDefault: false
  });

  // Proveedores predefinidos
  const providers = {
    gmail: {
      host: "smtp.gmail.com",
      port: 587,
      secure: false,
      requireTLS: true
    },
    outlook: {
      host: "smtp-mail.outlook.com",
      port: 587,
      secure: false,
      requireTLS: true
    },
    custom: {
      host: "",
      port: 587,
      secure: false,
      requireTLS: true
    }
  };

  /**
   * Abrir diálogo para crear nueva configuración
   */
  const handleOpenCreate = () => {
    setIsEditing(false);
    setFormData({
      name: "",
      description: "",
      provider: "custom",
      host: "",
      port: 587,
      secure: false,
      requireTLS: true,
      user: "",
      password: "",
      from: "",
      isActive: true,
      isDefault: false
    });
    setDialogOpen(true);
  };

  /**
   * Abrir diálogo para editar configuración
   */
  const handleOpenEdit = (config) => {
    setIsEditing(true);
    setSelectedConfig(config);
    setFormData({
      name: config.name || "",
      description: config.description || "",
      provider: config.provider || "custom",
      host: config.host || "",
      port: config.port || 587,
      secure: config.secure === 1 || config.secure === true,
      requireTLS: config.requireTLS === 1 || config.requireTLS === true,
      user: config.user || "",
      password: "", // No mostrar password existente
      from: config.from || "",
      isActive: config.isActive === 1 || config.isActive === true,
      isDefault: config.isDefault === 1 || config.isDefault === true
    });
    setDialogOpen(true);
  };

  /**
   * Cerrar diálogo
   */
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedConfig(null);
  };

  /**
   * Manejar cambio de proveedor (autocompletar campos)
   */
  const handleProviderChange = (e) => {
    const provider = e.target.value;
    const providerConfig = providers[provider];
    setFormData({
      ...formData,
      provider,
      host: providerConfig.host,
      port: providerConfig.port,
      secure: providerConfig.secure,
      requireTLS: providerConfig.requireTLS
    });
  };

  /**
   * Guardar configuración
   */
  const handleSave = async () => {
    if (!formData.name || !formData.host || !formData.user || !formData.from) {
      return;
    }
    if (!isEditing && !formData.password) {
      return;
    }
    try {
      const saved = isEditing && selectedConfig
        ? await updateConfig(selectedConfig.id, formData)
        : await createConfig(formData);
      if (saved) {
        handleCloseDialog();
      }
    } catch (error) {
      console.error("Error guardando configuración:", error);
    }
  };

  /**
   * Eliminar configuración
   */
  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar esta configuración SMTP?")) {
      return;
    }
    try {
      await deleteConfig(id);
    } catch (error) {
      console.error("Error eliminando configuración SMTP:", error);
    }
  };

  /**
   * Probar conexión
   */
  const handleTest = async (id) => {
    setTestModalOpen(true);
    setTestResult(null);
    
    try {
      const result = await testConnection(id);
      setTestResult({
        success: result.success,
        message: result.message || (result.success ? "Conexión exitosa" : "Error de conexión")
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || error.message || "Error desconocido al probar conexión"
      });
    }
  };

  /**
   * Cerrar modal de prueba
   */
  const handleCloseTestModal = () => {
    setTestModalOpen(false);
    setTestResult(null);
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h6" component="h2">
          {i18n.t("settings.smtpManager.pageTitle")}
        </Typography>
      </div>

      {/* Tabs internos */}
      <Paper className={classes.card} elevation={0}>
        <Tabs
          value={internalTab}
          onChange={(e, newValue) => setInternalTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabsContainer}
        >
          <Tab 
            icon={<SettingsIcon />} 
            label={i18n.t("settings.smtpManager.tabSmtp")} 
            value={0}
          />
          <Tab 
            icon={<EmailTemplateIcon />} 
            label={i18n.t("settings.smtpManager.tabTemplates")} 
            value={1}
          />
        </Tabs>

        {/* Panel: Configuraciones SMTP */}
        {internalTab === 0 && (
          <Box className={classes.tabPanel}>
            <div className={classes.header} style={{ marginBottom: 16 }}>
              <Typography variant="subtitle1" component="h3">
                {i18n.t("settings.smtpManager.sectionTitle")}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
              >
                {i18n.t("settings.smtpManager.newConfig")}
              </Button>
            </div>

            <Card className={classes.card}>
              <CardContent>
                {loading && configs.length === 0 ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : configs.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" align="center" p={3}>
                    {i18n.t("settings.smtpManager.emptyList")}
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table className={classes.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{i18n.t("settings.smtpManager.colName")}</TableCell>
                          <TableCell>{i18n.t("settings.smtpManager.colProvider")}</TableCell>
                          <TableCell>{i18n.t("settings.smtpManager.colHost")}</TableCell>
                          <TableCell>{i18n.t("settings.smtpManager.colPort")}</TableCell>
                          <TableCell>{i18n.t("settings.smtpManager.colUser")}</TableCell>
                          <TableCell>{i18n.t("settings.smtpManager.colStatus")}</TableCell>
                          <TableCell align="right">{i18n.t("settings.smtpManager.colActions")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {configs.map((config) => (
                          <TableRow key={config.id}>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" fontWeight={500}>
                                  {config.name}
                                </Typography>
                                {config.isDefault === 1 || config.isDefault === true ? (
                                  <Chip
                                    label={i18n.t("settings.smtpManager.defaultChip")}
                                    size="small"
                                    color="primary"
                                    className={classes.statusChip}
                                  />
                                ) : null}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={config.provider || "custom"}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{config.host}</TableCell>
                            <TableCell>{config.port}</TableCell>
                            <TableCell>{config.user}</TableCell>
                            <TableCell>
                              <Chip
                                icon={
                                  config.isActive === 1 || config.isActive === true ? (
                                    <CheckCircleIcon />
                                  ) : (
                                    <CancelIcon />
                                  )
                                }
                                label={
                                  config.isActive === 1 || config.isActive === true
                                    ? i18n.t("settings.smtpManager.statusActive")
                                    : i18n.t("settings.smtpManager.statusInactive")
                                }
                                size="small"
                                color={
                                  config.isActive === 1 || config.isActive === true
                                    ? "primary"
                                    : "default"
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={i18n.t("settings.smtpManager.tooltipTest")}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleTest(config.id)}
                                    disabled={testing}
                                  >
                                    <EmailIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={i18n.t("settings.smtpManager.tooltipEdit")}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEdit(config)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={i18n.t("settings.smtpManager.tooltipDelete")}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(config.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Panel: Plantillas de Email */}
        {internalTab === 1 && (
          <Box className={classes.tabPanel}>
            <EmailTemplateManager />
          </Box>
        )}
      </Paper>

      {/* Diálogo de creación/edición */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? i18n.t("settings.smtpManager.dialogEdit") : i18n.t("settings.smtpManager.dialogNew")}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={i18n.t("settings.smtpManager.fieldName")}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className={classes.formField}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth className={classes.formField}>
                <InputLabel>{i18n.t("settings.smtpManager.fieldProvider")}</InputLabel>
                <Select
                  value={formData.provider}
                  onChange={handleProviderChange}
                >
                  <MenuItem value="custom">{i18n.t("settings.smtpManager.providerCustom")}</MenuItem>
                  <MenuItem value="gmail">{i18n.t("settings.smtpManager.providerGmail")}</MenuItem>
                  <MenuItem value="outlook">{i18n.t("settings.smtpManager.providerOutlook")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={i18n.t("settings.smtpManager.fieldDescription")}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                multiline
                rows={2}
                className={classes.formField}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label={i18n.t("settings.smtpManager.fieldHost")}
                value={formData.host}
                onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                required
                className={classes.formField}
                placeholder="smtp.gmail.com"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={i18n.t("settings.smtpManager.fieldPort")}
                type="number"
                value={formData.port}
                onChange={(e) =>
                  setFormData({ ...formData, port: parseInt(e.target.value) || 587 })
                }
                required
                className={classes.formField}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={i18n.t("settings.smtpManager.fieldUser")}
                value={formData.user}
                onChange={(e) => setFormData({ ...formData, user: e.target.value })}
                required
                className={classes.formField}
                placeholder="usuario@gmail.com"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  fullWidth
                  label={i18n.t("settings.smtpManager.fieldPassword")}
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required={!isEditing}
                  className={classes.formField}
                  helperText={isEditing ? i18n.t("settings.smtpManager.passwordKeepHint") : ""}
                />
                {formData.provider === "gmail" && (
                  <Tooltip 
                    title={
                      <Box>
                        <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                          <span role="img" aria-label="Email">📧</span> {i18n.t("settings.smtpManager.gmailTooltipTitle")}
                        </Typography>
                        <Typography variant="body2" style={{ marginBottom: 8 }}>
                          {i18n.t("settings.smtpManager.gmailTooltipBody")}
                        </Typography>
                        <Typography variant="body2" style={{ marginBottom: 4, fontWeight: 'bold' }}>
                          {i18n.t("settings.smtpManager.gmailTooltipSteps")}
                        </Typography>
                        <Typography variant="body2" component="div" style={{ paddingLeft: 8, fontSize: '0.75rem' }}>
                          {i18n.t("settings.smtpManager.gmailTooltipStep1")}<br/>
                          {i18n.t("settings.smtpManager.gmailTooltipStep2")}<br/>
                          {i18n.t("settings.smtpManager.gmailTooltipStep3")}<br/>
                          {i18n.t("settings.smtpManager.gmailTooltipStep4")}<br/>
                          {i18n.t("settings.smtpManager.gmailTooltipStep5")}
                        </Typography>
                        <Link 
                          href="https://support.google.com/accounts/answer/185833" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#90caf9', fontSize: '0.75rem' }}
                        >
                          {i18n.t("settings.smtpManager.gmailGuideLink")}
                        </Link>
                      </Box>
                    }
                    arrow
                    placement="right"
                    interactive
                  >
                    <IconButton 
                      size="small" 
                      style={{ marginTop: -8 }}
                    >
                      <InfoIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={i18n.t("settings.smtpManager.fieldFrom")}
                value={formData.from}
                onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                required
                className={classes.formField}
                placeholder={i18n.t("settings.smtpManager.fieldFromPlaceholder")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.secure}
                    onChange={(e) =>
                      setFormData({ ...formData, secure: e.target.checked })
                    }
                  />
                }
                label={i18n.t("settings.smtpManager.sslLabel")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requireTLS}
                    onChange={(e) =>
                      setFormData({ ...formData, requireTLS: e.target.checked })
                    }
                    disabled={formData.secure}
                  />
                }
                label={i18n.t("settings.smtpManager.tlsLabel")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                }
                label={i18n.t("settings.smtpManager.activeLabel")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDefault}
                    onChange={(e) =>
                      setFormData({ ...formData, isDefault: e.target.checked })
                    }
                  />
                }
                label={i18n.t("settings.smtpManager.defaultLabel")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            {i18n.t("settings.smtpManager.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={loading || !formData.name || !formData.host || !formData.user || (!isEditing && !formData.password) || !formData.from}
          >
            {loading ? <CircularProgress size={20} /> : i18n.t("settings.smtpManager.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de resultado de prueba de conexión */}
      <Dialog
        open={testModalOpen}
        onClose={handleCloseTestModal}
        maxWidth="sm"
        fullWidth
        className={classes.errorModal}
      >
        <DialogTitle>
          {testResult ? (
            testResult.success ? (
              <span style={{ color: "#4caf50" }}>
                <span role="img" aria-label="ok">✅</span> {i18n.t("settings.smtpManager.testTitleSuccess")}
              </span>
            ) : (
              <span style={{ color: "#f44336" }}>
                <span role="img" aria-label="error">❌</span> {i18n.t("settings.smtpManager.testTitleFail")}
              </span>
            )
          ) : (
            i18n.t("settings.smtpManager.testTitlePending")
          )}
        </DialogTitle>
        <DialogContent>
          {testing && !testResult ? (
            <Box display="flex" flexDirection="column" alignItems="center" p={3}>
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: 16 }}>
                {i18n.t("settings.smtpManager.testVerifying")}
              </Typography>
            </Box>
          ) : testResult ? (
            <>
              {testResult.success ? (
                <Alert severity="success" style={{ marginBottom: 16 }}>
                  <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                    {i18n.t("settings.smtpManager.alertOkTitle")}
                  </Typography>
                  <Typography variant="body2">
                    {i18n.t("settings.smtpManager.alertOkBody")}
                  </Typography>
                </Alert>
              ) : (
                <>
                  <Alert severity="error" style={{ marginBottom: 16 }}>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                      {i18n.t("settings.smtpManager.alertErrTitle")}
                    </Typography>
                    <Typography variant="body2">
                      {i18n.t("settings.smtpManager.alertErrBody")}
                    </Typography>
                  </Alert>
                  <Box className={classes.errorContent}>
                    <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 'bold' }}>
                      {i18n.t("settings.smtpManager.errorDetailsLabel")}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      component="pre" 
                      style={{ 
                        whiteSpace: 'pre-wrap', 
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        margin: 0
                      }}
                    >
                      {testResult.message}
                    </Typography>
                  </Box>
                  {testResult.message && testResult.message.includes("BadCredentials") && (
                    <Box className={classes.infoBox} style={{ marginTop: 16 }}>
                      <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 'bold' }}>
                        <span role="img" aria-label="tip">💡</span> {i18n.t("settings.smtpManager.suggestedFixTitle")}
                      </Typography>
                      <Typography variant="body2">
                        {i18n.t("settings.smtpManager.suggestedFixBody")}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestModal} color="primary" variant="contained">
            {i18n.t("settings.smtpManager.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SMTPConfigManager;
